import React from 'react';
import { useNavigate } from 'react-router-dom';

const CarbonFarming = () => {
  const navigate = useNavigate();

  // External resources for carbon farming
  const resources = [
    {
      title: "Carbon Farming: A Climate Solution",
      source: "Nature Conservancy",
      description: "Explore how carbon farming practices can help mitigate climate change while improving soil health.",
      link: "https://www.nature.org/en-us/what-we-do/our-priorities/tackle-climate-change/climate-change-stories/natural-climate-solutions-science/"
    },
    {
      title: "The Carbon Farming Solution",
      source: "Regeneration International",
      description: "Learn about regenerative agricultural practices that sequester carbon and enhance ecosystem resilience.",
      link: "https://regenerationinternational.org/2020/09/28/regenerative-agriculture-and-the-soil-carbon-solution-new-paper-outlines-vision-for-climate-action/"
    },
    {
      title: "Carbon Farming: Co-benefits for Farmers and Climate",
      source: "FAO",
      description: "Discover how carbon farming creates economic opportunities for farmers while addressing climate challenges.",
      link: "https://www.fao.org/climate-smart-agriculture/on-the-ground/europe/en/"
    }
  ];

  // Key carbon farming practices
  const practices = [
    {
      title: "No-Till Agriculture",
      description: "Minimizes soil disturbance to preserve carbon in soil",
      icon: "🌱"
    },
    {
      title: "Cover Crops",
      description: "Plants that protect soil between harvest and planting seasons",
      icon: "🌿"
    },
    {
      title: "Rotational Grazing",
      description: "Moving livestock systematically to allow pasture recovery",
      icon: "🐄"
    },
    {
      title: "Agroforestry",
      description: "Integrating trees with crops or livestock systems",
      icon: "🌳"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50 p-6 md:p-12">
      {/* Header with blur effect */}
      <div className="sticky top-0 z-10 backdrop-blur-lg bg-white/70 rounded-2xl p-6 mb-8 shadow-sm">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
          Understanding Carbon Farming
        </h1>
        <p className="text-gray-600 mt-3 max-w-3xl">
          Carbon farming enhances the capture of carbon dioxide from the atmosphere into soil and plant material, helping mitigate climate change while improving agricultural productivity.
        </p>
      </div>

      {/* Main content */}
      <div className="max-w-6xl mx-auto grid gap-8">
        {/* Introduction section */}
        <section className="bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-md">
          <h2 className="text-2xl font-semibold bg-gradient-to-r from-green-500 to-teal-500 bg-clip-text text-transparent mb-4">What is Carbon Farming?</h2>
          <p className="text-gray-700 mb-4">
            Carbon farming refers to a range of agricultural practices that sequester carbon dioxide from the atmosphere and store it in the soil and vegetation. This approach not only helps combat climate change but also improves soil health, water retention, and crop resilience.
          </p>
          <p className="text-gray-700">
            As a nature-based solution to climate change, carbon farming offers multiple co-benefits including enhanced biodiversity, reduced erosion, and improved farm productivity.
          </p>
        </section>

        {/* Key practices section */}
        <section className="bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-md">
          <h2 className="text-2xl font-semibold bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent mb-6">Key Carbon Farming Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {practices.map((practice, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-6 shadow-sm transition-all hover:shadow-md">
                <div className="text-4xl mb-3">{practice.icon}</div>
                <h3 className="text-xl font-medium text-green-700">{practice.title}</h3>
                <p className="text-gray-600 mt-2">{practice.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits visualization */}
        <section className="bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-md">
          <h2 className="text-2xl font-semibold text-green-600 mb-6">Benefits of Carbon Farming</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-300/10 to-teal-300/20 rounded-2xl p-6 text-center">
              <h3 className="text-lg font-medium text-green-700 mb-2">Environmental</h3>
              <ul className="text-gray-700 space-y-2 text-left">
                <li>• Reduced greenhouse gas emissions</li>
                <li>• Enhanced biodiversity</li>
                <li>• Improved water quality</li>
                <li>• Reduced soil erosion</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-300/10 to-teal-300/20 rounded-2xl p-6 text-center">
              <h3 className="text-lg font-medium text-green-700 mb-2">Agricultural</h3>
              <ul className="text-gray-700 space-y-2 text-left">
                <li>• Enhanced soil fertility</li>
                <li>• Improved crop yields</li>
                <li>• Better drought resilience</li>
                <li>• Reduced input costs</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-300/10 to-teal-300/20 rounded-2xl p-6 text-center">
              <h3 className="text-lg font-medium text-green-700 mb-2">Economic</h3>
              <ul className="text-gray-700 space-y-2 text-left">
                <li>• Carbon credit opportunities</li>
                <li>• Premium product markets</li>
                <li>• Reduced fertilizer costs</li>
                <li>• Long-term farm sustainability</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Resources section */}
        <section className="bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-md">
          <h2 className="text-2xl font-semibold text-green-600 mb-6">Learn More</h2>
          <div className="space-y-6">
            {resources.map((resource, index) => (
              <a 
                key={index}
                href={resource.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-green-50 to-white rounded-2xl p-6 shadow-sm transition-all hover:shadow-md hover:translate-x-1"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-medium text-green-700">{resource.title}</h3>
                    <p className="text-green-600 text-sm mt-1">Source: {resource.source}</p>
                    <p className="text-gray-600 mt-3">{resource.description}</p>
                  </div>
                  <div className="text-green-500 ml-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="7" y1="17" x2="17" y2="7"></line>
                      <polyline points="7 7 17 7 17 17"></polyline>
                    </svg>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Call to action */}
        <section className="bg-gradient-to-r from-green-400 to-teal-400 rounded-3xl p-8 shadow-md text-white">
          <h2 className="text-2xl font-semibold mb-4">Ready to Start Carbon Farming?</h2>
          <p className="mb-6">Join the growing community of farmers and land managers implementing carbon-smart practices to benefit your land, livelihood, and the planet.</p>
          <button 
            className="bg-white text-green-600 py-3 px-6 rounded-full font-medium hover:bg-green-50 transition-colors shadow-sm"
            onClick={() => navigate('/calculator')}
          >
            Get Started
          </button>
        </section>
      </div>

      {/* Footer */}
    </div>
  );
};

export default CarbonFarming;